import React, { useState, useEffect, useCallback } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parse, format } from "date-fns";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import MainLayout from "../Themes/MainLayout";
import MuiAlert from "@mui/material/Alert";
import { hasPermission } from "../utils/auth";
import { formatDateLocal } from "../utils/dateUtils";

import {
  CircularProgress,
  TextField,
  FormHelperText,
  Grid,
  Pagination,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Popover,
  InputAdornment,
  Switch,
  Typography,
  Container,
  CssBaseline,
  OutlinedInput,
  Popper,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { Add, Search, FilterList } from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import CloseIcon from "@mui/icons-material/Close";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const empProjectsFields = [
  "employee_id",
  "cl_name",
  "proj_id",
  "assigned_from",
  "assigned_to",
  "projectmanagerid",
  "technicalleadid",
];

const empProjectFormFields = [
  "cl_id",
  "proj_id",
  "employee_id",
  "assigned_from",
  "assigned_to",
];

const empfieldLabels = {
  bo_id: "Organization",
  employee_id: "Employee Name",
  cl_id: "Client",
  cl_name: "Client Name",
  proj_id: "Project",
  assigned_from: "Assigned From",
  assigned_to: "Assigned To",
  allocation_percentage: "Allocation Percentage",
  projectmanagerid: "Project Manager",
  technicalleadid: "Technical Lead",
};

const initialEmpPjctState = {
  bo_id: "",
  employee_id: "",
  proj_id: "",
  assigned_from: "",
  assigned_to: "",
  allocation_percentage: "",
};

const EmployeeProjects = () => {
  const { loading, userId, empId, boId, roles, hasPermission } =
    useCurrentUser();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [empPjct, setEmpPjct] = useState([]);
  const [filters, setFilters] = useState({ deleteFlag: "ACTIVE" });
  const [anchorEls, setAnchorEls] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {};
    empProjectsFields.forEach((f) => (initial[f] = true));
    return initial;
  });
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pjctName, setPjctName] = useState({});
  const [empName, setEmpName] = useState({});
  const [newEmpPjct, setNewEmpPjct] = useState({
    bo_id: boId,
    employee_id: "",
    proj_id: "",
    cl_id: "",
    assigned_from: "",
    assigned_to: "",
    allocation_percentage: "",
    created_by: empId,
    modified_by: empId,
  });
  const [empProjEdit, setEditEmpProj] = useState({});
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [search, setSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [deleteId, setDeleteId] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const rowsPerPage = 10;
  const employee_id = empId;
  const bo_id = boId;
  function formatDateForDB(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // yyyy-mm-dd
  }
  const fetchempPjct = useCallback(async () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v && v.trim() !== "")
    );
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        search: search.trim(),
        bo_id,
        deleteFlag: filters.deleteFlag || "",
        empId,
        roles,
        ...Object.fromEntries(
          Object.entries(cleanFilters).map(([k, v]) => [k, v.toString()])
        ),
      }).toString();
      const response = await fetch(
        `${BASE_URL}/employee_projects/paginate/list?${params}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Fetch failed");
      setEmpPjct(data.data);
      setTotalCount(data.total);
    } catch (err) {
      console.error("Error fetching employee projects:", err);
      setSnackbarMsg(err.message || "Failed to fetch employee projects");
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [page, filters, search, bo_id]);
  useEffect(() => {
    const fetchStatusFlags = async () => {
      try {
        const res = await fetch(`${BASE_URL}/commonVariables/statusFlag`);
        const data = await res.json();
        setStatusOptions(data); // [{ code: "ACTIVE", label: "Active" }, ...]
      } catch (err) {
        console.error("Failed to fetch employee status flags:", err);
      }
    };

    fetchStatusFlags();
  }, []);
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/employees/${boId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Fetch failed");
      setEmployees(data);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setSnackbarMsg(err.message || "Failed to fetch employees");
      setShowSnackbar(true);
    }
  }, []);

  const fetchClientAndProjects = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/employee_projects/${boId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Fetch failed");
      setClients(data.clients);
      setProjects(data.projects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setErrorMessage(`Failed to fetch projects: ${err.message}`);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchClientAndProjects();
  }, [fetchEmployees, fetchClientAndProjects]);

  useEffect(() => {
    fetchempPjct();
  }, [fetchempPjct]);

  useEffect(() => {
    setPage(0);
  }, [search, filters, boId]);

  const renderField = (field, state, setState, isEditMode = false) => {
    const handleChange = (e) => {
      const { name, value } = e.target;
      let processedValue = value;
      if (field === "allocation_percentage")
        processedValue = parseFloat(value) || "";
      else if (field === "assigned_from" || field === "assigned_to")
        processedValue = value;
      setState((prev) => ({
        ...prev,
        [name]: processedValue,
      }));
      //  Clear inline error for the current field
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    };
    if (field === "employee_id") {
      return (
        <Autocomplete
          id="employee_id"
          options={employees}
          getOptionLabel={(option) =>
            option && option.employee_id
              ? `${option.employee_id} - ${option.first_name} ${option.last_name}`
              : ""
          }
          value={
            employees.find((e) => e.employee_id === state.employee_id) || null
          }
          onChange={(event, newValue) => {
            setState((prev) => ({
              ...prev,
              employee_id: newValue ? newValue.employee_id : "",
            }));
            // Clear inline error for employee
            setErrors((prev) => ({
              ...prev,
              employee_id: "",
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employee *"
              margin="none"
              error={!!errors.employee_id}
              helperText={errors.employee_id || ""}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: "250px",
                //  Outlined border + notch fix
                "& .MuiOutlinedInput-root": {
                  height: "35px",
                  fontSize: "12px",
                  "& legend": {
                    maxWidth: "75px", //  removes extra right-side notch space
                  },
                  "& .MuiInputBase-input": {
                    padding: "10px 14px",
                  },
                },
                //  Fix label position (same style you requested)
                "& .MuiInputLabel-outlined": {
                  transform: "translate(12px, -10px) scale(0.75)",
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography
                sx={{
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {`${option.employee_id} - ${option.first_name} ${option.last_name}`}
              </Typography>
            </li>
          )}
          PopperComponent={(props) => (
            <Popper
              {...props}
              sx={{
                minWidth: "260px",
                maxHeight: "200px",
                overflow: "auto",
              }}
            />
          )}
          ListboxComponent={(props) => (
            <ul {...props} style={{ maxHeight: "200px", overflow: "auto" }} />
          )}
          disabled={employees.length === 0 || isEditMode}
        />
      );
    }

    if (field === "cl_id") {
      return (
        <FormControl
          sx={{
            width: "250px",
            //  Outlined border + notch fix
            "& .MuiOutlinedInput-root": {
              height: "35px",
              fontSize: "12px",
              "& legend": {
                maxWidth: "40px", //  removes extra right-side notch space
              },
              "& .MuiInputBase-input": {
                padding: "10px 14px",
              },
            },
            //  Fix label position (same style you requested)
            "& .MuiInputLabel-outlined": {
              transform: "translate(12px, -10px) scale(0.75)",
            },
          }}
          margin="none"
          required
          error={!!errors.cl_id}
        >
          <InputLabel shrink>Client</InputLabel>
          <Select
            value={state.cl_id || ""}
            onChange={(e) => {
              const value = e.target.value;
              setState((prev) => ({
                ...prev,
                cl_id: e.target.value,
                proj_id: "",
              }));
              // Clear inline error for client
              setErrors((prev) => ({
                ...prev,
                cl_id: "",
              }));
            }}
            name="cl_id"
            input={<OutlinedInput notched label="Client " />}
            disabled={isLoading}
          >
            {clients.map((client) => (
              <MenuItem
                key={client.cl_id}
                value={client.cl_id}
                sx={{ fontSize: "12px" }}
              >
                {client.cl_name}
              </MenuItem>
            ))}
          </Select>
          {errors.cl_id && <FormHelperText>{errors.cl_id}</FormHelperText>}
        </FormControl>
      );
    }

    if (field === "proj_id") {
      const filteredProjects = projects.filter(
        (proj) => proj.cl_id === state.cl_id
      );

      return (
        <FormControl
          sx={{
            width: "250px",
            //  Outlined border + notch fix
            "& .MuiOutlinedInput-root": {
              height: "35px",
              fontSize: "12px",
              "& legend": {
                maxWidth: "45px", //  removes extra right-side notch space
              },
              "& .MuiInputBase-input": {
                padding: "10px 14px",
              },
            },
            //  Fix label position (same style you requested)
            "& .MuiInputLabel-outlined": {
              transform: "translate(12px, -10px) scale(0.75)",
            },
          }}
          margin="none"
          required
          error={!!errors.proj_id}
        >
          <InputLabel shrink>Project</InputLabel>
          <Select
            value={state.proj_id || ""}
            onChange={(e) => {
              handleChange(e);
              setErrors((prev) => ({ ...prev, proj_id: "" }));
            }}
            name="proj_id"
            input={<OutlinedInput notched label="Project" />}
            disabled={isLoading || !state.cl_id}
          >
            {filteredProjects.map((proj) => (
              <MenuItem
                key={proj.proj_id}
                value={proj.proj_id}
                sx={{ fontSize: "12px" }}
              >
                {proj.project_name}
              </MenuItem>
            ))}
          </Select>
          {errors.proj_id && <FormHelperText>{errors.proj_id}</FormHelperText>}
        </FormControl>
      );
    }

    if (field === "assigned_from" || field === "assigned_to") {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={empfieldLabels[field]}
            value={state[field] ? new Date(state[field]) : null}
            onChange={(newValue) => {
              setState((prev) => ({
                ...prev,
                [field]: newValue,
              }));
              setErrors((prev) => ({ ...prev, [field]: "" }));
            }}
            format="MM/dd/yyyy"
            slotProps={{
              textField: {
                required: true,
                error: false,
                name: field,
                placeholder: "MM/DD/YYYY",
                error: !!errors[field],
                helperText: errors[field] || "",
                FormHelperTextProps: {
                  sx: {
                    position: "absolute",
                    bottom: -18, // adjust as needed
                    left: 0,
                    fontSize: "10px",
                    color: "red",
                  },
                },
                InputLabelProps: { shrink: true },
                InputProps: {
                  sx: {
                    fontSize: "12px",
                    height: "35px",
                    backgroundColor: "#fafafa",
                  },
                },
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    paddingLeft: 1,
                    paddingRight: 10,
                  },

                  "& .MuiInputLabel-outlined": {
                    transform: "translate(12px, -10px) scale(0.75)", // FIXED LABEL POSITION
                    padding: 0,
                  },
                },
                margin: "none",
              },
              popper: {
                disablePortal: false,
                placement: "bottom-start",
              },
            }}
          />
        </LocalizationProvider>
      );
    }
    if (field === "allocation_percentage") {
      return (
        <TextField
          key={field}
          label={empfieldLabels[field]}
          name={field}
          value={state[field] || ""}
          onChange={handleChange}
          type="number"
          fullWidth
          size="small"
          required
          disabled={isEditMode}
          InputLabelProps={{ shrink: true, sx: { fontSize: "12px" } }}
          sx={{
            fontSize: "12px",
            height: "40px",
            width: "250px",
            "& .MuiInputBase-input": { fontSize: "12px", padding: "10px 14px" },
          }}
          inputProps={{ min: 0, max: 100, step: 0.01 }}
        />
      );
    }

    if (field === "emp_proj_id" && isEditMode) {
      return (
        <FormControl
          sx={{
            width: "250px",
            //  Outlined border + notch fix
            "& .MuiOutlinedInput-root": {
              height: "35px",
              fontSize: "12px",
              "& legend": {
                maxWidth: "60px", //  removes extra right-side notch space
              },
              "& .MuiInputBase-input": {
                padding: "10px 14px",
              },
            },
            //  Fix label position (same style you requested)
            "& .MuiInputLabel-outlined": {
              transform: "translate(12px, -14px) scale(0.75)",
            },
          }}
          margin="none"
        >
          <InputLabel
            shrink
            sx={{
              fontSize: "12px",
              position: "absolute",
              top: "4px",
              left: "4px",
            }}
          >
            Employee Project ID
          </InputLabel>
          <OutlinedInput
            value={state[field] || ""}
            name={field}
            label="Employee Project ID"
            disabled
          />
        </FormControl>
      );
    }

    return null;
  };

  const handleAdd = async () => {
    const newErrors = {};
    if (!newEmpPjct.cl_id) {
      newErrors.cl_id = "Client is required";
    }
    if (!newEmpPjct.proj_id) {
      newErrors.proj_id = "Project is required";
    }
    if (!newEmpPjct.employee_id) {
      newErrors.employee_id = "Employee name required";
    }
    if (!newEmpPjct.assigned_from || !newEmpPjct.assigned_to) {
      newErrors.assigned_from = "Please select assigned from date";
      newErrors.assigned_to = "Please select assigned to date";
    }
    const fromDate = newEmpPjct.assigned_from
      ? formatDateForDB(newEmpPjct.assigned_from)
      : null;
    const toDate = newEmpPjct.assigned_to
      ? formatDateForDB(newEmpPjct.assigned_to)
      : null;

    //  Show only in Snackbar if fromDate > toDate
    if (fromDate && toDate && fromDate > toDate) {
      setSnackbarMsg(
        "Assigned From date must be before or equal to Assigned To date"
      );
      setShowSnackbar(true);
      return;
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    try {
      const response = await fetch(`${BASE_URL}/employee_projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...newEmpPjct,
          assigned_from: fromDate, // yyyy-mm-dd
          assigned_to: toDate, //  yyyy-mm-dd
          bo_id: boId,
          created_by: empId,
          modified_by: empId,
        }),
      });
      const data = await response.json();
      // if (!response.ok) throw new Error(data.message || "Add failed");
      if (!response.ok) {
        // Show backend message in Snackbar instead of throwing error
        setSnackbarMsg(data.message || "Failed to add employee project");
        setShowSnackbar(true);
        return; // stop further execution
      }
      setSuccessMessage("Project assigned to the employee successfully!");
      setTimeout(() => setSuccessMessage(""), 5000);
      setOpen(false);
      setNewEmpPjct({
        bo_id: boId,
        employee_id: "",
        proj_id: "",
        assigned_from: "",
        assigned_to: "",
        allocation_percentage: "",
      });
      fetchempPjct();
    } catch (err) {
      console.error("Add failed:", err.message);
      setErrorMessage(`Failed to add employee project: ${err.message}`);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleEdit = async (row) => {
    const fromDate = formatDateForDB(empProjEdit.assigned_from);
    const toDate = formatDateForDB(empProjEdit.assigned_to);
    if (fromDate > toDate) {
      setSnackbarMsg(
        "Assigned From date must be less than or equal to Assigned To date"
      );
      setShowSnackbar(true);
      return;
    }
    try {
      const response = await fetch(
        `${BASE_URL}/employee_projects/${empProjEdit.emp_proj_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...empProjEdit,
            assigned_from: fromDate, // yyyy-mm-dd
            assigned_to: toDate, //  yyyy-mm-dd
            bo_id: boId,
            modified_by: empId,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message || "This project already assigned to the same employee"
        );
      }

      setEditOpen(false);
      setSuccessMessage("Project updated successfully!");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempPjct();
    } catch (err) {
      console.error("Update failed:", err.message);
      setErrorMessage(`Failed to add employee project: ${err.message}`);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleDelete = (emp_proj_id, project_name, employee_name) => {
    setDeleteId(emp_proj_id);
    setPjctName(project_name);
    setEmpName(employee_name);
    setConfirmOpen(true);
  };

  const confirmDelete = async (emp_proj_id) => {
    try {
      const response = await fetch(
        `${BASE_URL}/employee_projects/${deleteId}/${bo_id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Delete failed");
      setSuccessMessage("Record deleted successfuly..");
      setTimeout(() => setSuccessMessage(""), 5000);

      fetchempPjct();
    } catch (err) {
      console.error("Delete failed:", err.message);
      setErrorMessage(`Failed to delete: ${err.message}`);
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const handleFlagClick = async (emp_proj_id, currentFlag) => {
    const newFlag = currentFlag === "Y" ? "N" : "Y";
    try {
      const response = await fetch(
        `${BASE_URL}/employee_projects/${emp_proj_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bo_id: boId,
            delete_flag: newFlag,
            modified_by: empId,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Flag update failed");
      setSuccessMessage("Toggle Flag updated Successfully..");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempPjct();
    } catch (err) {
      console.error("Error updating flag:", err);
      setErrorMessage("Failed to update Flag.");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleEditClick = (row) => {
    setEditEmpProj({
      ...row,
      cl_id: row.cl_id,
      proj_id: row.proj_id,
      employee_id: row.employee_id,
      assigned_from: row.assigned_from || "",
      assigned_to: row.assigned_to || "",
    });
    setEditOpen(true);
  };

  const handleFilterIconClick = (field, event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleFilterClose = (field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };
  return (
    <MainLayout>
      <Typography variant="h5" gutterBottom fontSize="25px" fontWeight="600">
        Employee Projects
      </Typography>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        mt={0.5}
        mb={2}
        gap={2}
      >
        <IconButton onClick={(e) => setColumnAnchorEl(e.currentTarget)}>
          <SettingsIcon />
        </IconButton>
        <Popover
          open={Boolean(columnAnchorEl)}
          anchorEl={columnAnchorEl}
          onClose={() => setColumnAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 1,
              width: 200,
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: 2,
            },
          }}
        >
          <Box p={1} display="flex" flexDirection="column" gap={1}>
            {empProjectsFields.map((field) => (
              <Box
                key={field}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  color: "black",
                  border: "1px solid #ccc",
                  borderRadius: 1.5,
                  px: 2,
                  py: 1,
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",

                    color: "#333",
                    fontWeight: "bold",
                  }}
                >
                  {empfieldLabels[field]}
                </Typography>
                <Switch
                  checked={columnVisibility[field]}
                  onChange={() =>
                    setColumnVisibility((prev) => ({
                      ...prev,
                      [field]: !prev[field],
                    }))
                  }
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2DB0E4",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Popover>
        {hasPermission("Employee Projects", "Edit") && (
          <Button
            variant="contained"
            onClick={() => {
              setOpen(true);
              setNewEmpPjct(initialEmpPjctState);
              setErrors({});
            }}
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2.5,
              height: 36,
              backgroundColor: "#2DB0E4",
            }}
            startIcon={<Add />}
          >
            Add
          </Button>
        )}
        <TextField
          size="small"
          sx={{
            width: 250,
            "& input": { fontSize: 14 }, // input + placeholder
            "& input::placeholder": { fontSize: 12 }, // placeholder only
          }}
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            sx: { height: 36, paddingRight: 0 },
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {/* Status Dropdown */}
        <TextField
          select
          size="small"
          label="Status"
          value={filters.deleteFlag || ""}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, deleteFlag: e.target.value }))
          }
          sx={{
            width: 200,
            // Make height match the 36px of the search field
            "& .MuiInputBase-root": {
              height: 36,
            },

            // Fix vertical alignment of text
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              padding: "0 14px",
            },

            // Label font size
            "& .MuiInputLabel-root": { fontSize: "14px" },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    minHeight: "32px",
                  },
                },
              },
            },
          }}
          InputProps={{ sx: { height: 35, alignItems: "center" } }}
        >
          <MenuItem value="All">All</MenuItem>
          {statusOptions.map((opt) => (
            <MenuItem key={opt.code} value={opt.code}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}>
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    Actions
                  </TableCell>
                  {empProjectsFields
                    .filter((f) => columnVisibility[f])
                    .map((field) => (
                      <TableCell key={field}>
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          sx={{ gap: 0.5 }}
                        >
                          {empfieldLabels[field]}
                          <IconButton
                            size="small"
                            onClick={(e) => handleFilterIconClick(field, e)}
                          >
                            <FilterList fontSize="small" />
                          </IconButton>
                          <Popover
                            open={Boolean(anchorEls[field])}
                            anchorEl={anchorEls[field]}
                            onClose={() => handleFilterClose(field)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                          >
                            <Box
                              p={2}
                              display="flex"
                              flexDirection="column"
                              gap={1}
                            >
                              <TextField
                                size="small"
                                fullWidth
                                placeholder={`Filter ${empfieldLabels[field]}`}
                                value={filters[field] || ""}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    [field]: e.target.value,
                                  }))
                                }
                                sx={{
                                  fontSize: "12px",
                                  width: "200px",
                                  "& .MuiInputBase-input": { fontSize: "12px" },
                                }}
                              />
                              <Box
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleFilterClose(field)}
                                  sx={{ minWidth: 70 }}
                                >
                                  Apply
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setFilters((prev) => ({
                                      ...prev,
                                      [field]: "",
                                    }));
                                    handleFilterClose(field);
                                  }}
                                  sx={{ minWidth: 70 }}
                                >
                                  Clear
                                </Button>
                              </Box>
                            </Box>
                          </Popover>
                        </Box>
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        empProjectsFields.filter((f) => columnVisibility[f])
                          .length + 1
                      }
                      align="center"
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : empPjct.length > 0 ? (
                  empPjct.map((row) => (
                    <TableRow key={row.emp_proj_id}>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip
                            title={
                              hasPermission("Employee Projects", "Edit")
                                ? "Edit"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Employee Projects", "Edit") &&
                                  handleEditClick(row)
                                }
                                disabled={
                                  !hasPermission("Employee Projects", "Edit")
                                }
                                sx={{
                                  color: hasPermission(
                                    "Employee Projects",
                                    "Edit"
                                  )
                                    ? "#2DB0E4"
                                    : "#ccc",
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Employee Projects", "Delete")
                                ? "Delete"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission(
                                    "Employee Projects",
                                    "Delete"
                                  ) &&
                                  handleDelete(
                                    row.emp_proj_id,
                                    row.project_name,
                                    row.employee_name
                                  )
                                }
                                disabled={
                                  !hasPermission("Employee Projects", "Delete")
                                }
                              >
                                <DeleteIcon
                                  sx={{
                                    color: hasPermission(
                                      "Employee Projects",
                                      "Delete"
                                    )
                                      ? "red"
                                      : "#ccc",
                                    width: 15,
                                    height: 15,
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip
                            title={
                              hasPermission("Employee Projects", "Edit")
                                ? "Toggle Delete Flag"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Employee Projects", "Edit") &&
                                  handleFlagClick(
                                    row.emp_proj_id,
                                    row.delete_flag
                                  )
                                }
                                disabled={
                                  !hasPermission("Employee Projects", "Edit")
                                }
                              >
                                <FlagIcon
                                  sx={{
                                    color:
                                      row.delete_flag === "N"
                                        ? "#2E9F50"
                                        : "#FF0C0C",
                                    width: 17,
                                    height: 17,
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {empProjectsFields
                        .filter((f) => columnVisibility[f])
                        .map((field) => (
                          <TableCell
                            key={field}
                            sx={{
                              textAlign: "left",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {field === "employee_id"
                              ? row.employee_name || row.employee_id || "-"
                              : field === "cl_name"
                              ? row.cl_name || "-"
                              : field === "proj_id"
                              ? row.project_name || row.proj_id || "-"
                              : field === "assigned_from" ||
                                field === "assigned_to"
                              ? formatDateLocal(row[field]) || "-"
                              : field === "projectmanagerid"
                              ? row.projectmanager_name ||
                                row.projectmanagerid ||
                                "-"
                              : field === "technicalleadid"
                              ? row.technicallead_name ||
                                row.technicalleadid ||
                                "-"
                              : row[field] || "-"}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={
                        empProjectsFields.filter((f) => columnVisibility[f])
                          .length + 1
                      }
                      align="center"
                    >
                      No matching data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(totalCount / rowsPerPage)}
              page={page + 1}
              onChange={(_, newPage) => setPage(newPage - 1)}
              color="primary"
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "8px",
                  minWidth: "32px",
                  height: "32px",
                  fontSize: "14px",
                },
                "& .Mui-selected": {
                  backgroundColor: "#b3dbea !important",
                  color: "black",
                  fontWeight: "bold",
                },
                "& .MuiPaginationItem-ellipsis": {
                  display: "none",
                },
              }}
            />
          </Box>
        </ThemeProvider>
      </Box>

      <ThemeProvider theme={FormThemes}>
        <CssBaseline />
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            {`Are you sure you want to permanently delete ${pjctName} project for the
            employee - ${empName}`}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullWidth
          maxWidth="sm"
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: 5,
              overflow: "visible",
              position: "absolute",
              top: 50,
              left: "50%",
              transform: "translate(-50%, 0)",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderRadius: "12px",
              bgcolor: "#f5f5f5",
              p: 1,
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Add Employee Project
            <IconButton
              aria-label="close"
              onClick={() => setOpen(false)}
              sx={{ position: "absolute", right: 8, top: 5 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: "14px 34px",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              backgroundColor: "#fff",
              mt: 1,
            }}
          >
            <Grid
              container
              rowSpacing={2}
              columnSpacing={1}
              sx={{ width: "100%", p: 1 }}
            >
              {empProjectFormFields.map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  {renderField(field, newEmpPjct, setNewEmpPjct)}
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setOpen(false);
                setErrors({});
              }}
              variant="outlined"
              color="inherit"
              sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              variant="contained"
              sx={{ backgroundColor: "#2DB0E4" }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          fullWidth
          maxWidth="sm"
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: 5,
              overflow: "visible",
              position: "absolute",
              top: 50,
              left: "50%",
              transform: "translate(-50%, 0)",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderRadius: "12px",
              bgcolor: "#f5f5f5",
              p: 1,
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Update Employee Project
            <IconButton
              aria-label="close"
              onClick={() => setEditOpen(false)}
              sx={{ position: "absolute", right: 8, top: 5 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              p: "14px 34px",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              backgroundColor: "#fff",
              mt: 1,
            }}
          >
            <Grid
              container
              rowSpacing={2}
              columnSpacing={1}
              sx={{ width: "100%", p: 1 }}
            >
              {[...empProjectFormFields].map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  {renderField(field, empProjEdit, setEditEmpProj, true)}
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
            <Button
              onClick={() => setEditOpen(false)}
              variant="outlined"
              color="inherit"
              sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              variant="contained"
              sx={{
                backgroundColor: "#4CAF50",
                "&:hover": { backgroundColor: "#45a049" },
              }}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarMsg.includes("Failed") ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default EmployeeProjects;
